import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wallet, CheckCircle2, ArrowUpRight, Loader2 } from "lucide-react";

export default function PayoutsCard() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [busy, setBusy] = useState(false);

  const formatDate = (iso) => {
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
  };

  const load = () => {
    api.get("/connect/status").then((r) => setStatus(r.data)).catch(() => setStatus({ has_account: false, charges_enabled: false }));
    api.get("/connect/earnings").then((r) => setEarnings(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    if (params.get("payouts")) {
      const p = new URLSearchParams(params);
      p.delete("payouts");
      setParams(p, { replace: true });
    }
  }, []);

  const onboard = async () => {
    setBusy(true);
    try {
      const res = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payout setup");
      setBusy(false);
    }
  };

  const release = async () => {
    setBusy(true);
    try {
      const res = await api.post("/connect/release-payouts");
      const d = res.data;
      const extra = d.held_back > 0 && d.next_available_date ? ` · €${d.held_back} available ${d.next_available_date}` : "";
      toast.success(`Withdrew €${d.released} to your bank${extra}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not release earnings");
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const enabled = status.charges_enabled;
  const nudge = !enabled && earnings && earnings.pending > 0;

  return (
    <div data-testid="payouts-card" className={`rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between border ${nudge ? "bg-[#FFF6F0] border-[#FF5A00]/40" : "bg-white border-black/10"}`}>
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${enabled ? "bg-[#2E7D32]/10" : "bg-[#FF5A00]/10"}`}>
          <Wallet className={`w-5 h-5 ${enabled ? "text-[#2E7D32]" : "text-[#FF5A00]"}`} />
        </div>
        <div>
          <p className="font-display font-bold tracking-tight flex items-center gap-1.5">
            {nudge ? `€${earnings.pending} waiting for you` : "Payouts"} {enabled && <CheckCircle2 className="w-4 h-4 text-[#2E7D32]" />}
          </p>
          {enabled ? (
            <>
              <p className="text-sm text-muted-foreground">
                Enabled — you receive payouts automatically after each transaction.
                {earnings && <> Pending <b>€{earnings.pending}</b> · Paid out <b>€{earnings.paid_out}</b></>}
              </p>
              {earnings && earnings.pending > 0 && (
                <div data-testid="payout-settlement-note" className="text-xs text-muted-foreground/80 mt-1.5 max-w-md space-y-0.5">
                  {earnings.available_now > 0 && (
                    <p data-testid="payout-available-now">
                      <b className="text-[#2E7D32]">€{earnings.available_now}</b> available to withdraw now.
                    </p>
                  )}
                  {earnings.available_later > 0 && earnings.next_available_date && (
                    <p data-testid="payout-available-later">
                      <b>€{earnings.available_later}</b> becomes available on{" "}
                      <b>{formatDate(earnings.next_available_date)}</b> — card payments take a few business days to settle into your Stripe balance.
                    </p>
                  )}
                  {earnings.available_now === 0 && earnings.available_later === 0 && (
                    <p>Card payments take a few business days to settle before they can be withdrawn.</p>
                  )}
                </div>
              )}
            </>
          ) : nudge ? (
            <p data-testid="payout-nudge" className="text-sm text-foreground/80 max-w-md">
              You've earned <b>€{earnings.pending}</b> from your rentals and sales. Set up payouts (takes ~2 minutes) to transfer it to your bank.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground max-w-md">
              Set up payouts to receive money from your rentals and sales. Required before your first payout.
            </p>
          )}
        </div>
      </div>

      <div className="shrink-0">
        {!enabled ? (
          <button data-testid="setup-payouts-btn" onClick={onboard} disabled={busy} className="flex items-center gap-1.5 bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />} {nudge ? "Set up payouts & get paid" : "Set up payouts"}
          </button>
        ) : earnings && earnings.available_now > 0 ? (
          <button data-testid="release-payouts-btn" onClick={release} disabled={busy} className="flex items-center gap-1.5 bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />} Withdraw €{earnings.available_now}
          </button>
        ) : earnings && earnings.pending > 0 ? (
          <span data-testid="payout-settling" className="text-sm font-medium text-muted-foreground">Settling…</span>
        ) : (
          <span className="text-sm font-medium text-[#2E7D32]">Ready to receive</span>
        )}
      </div>
    </div>
  );
}
